import {
    IMcsaDualAnswer,
    IMcsaGameCatalogItem,
    IMcsaMapPoint,
    IMcsaQuestion,
    IMcsaWorld,
    IMcsaWorldGame,
    TMcsaAnswer,
    TMcsaQuestionMech,
    TMcsaWorldGamePhase,
    TMcsaWorldKey,
    TMcsaWorldStatus,
} from '../types/mcsa.interface';
import { MCSA_MAP_START, MCSA_WORLD_LAYOUT } from './mcsa-journey.layout';

export const getWorldStatus = (
    worlds: IMcsaWorld[],
    completedKeys: TMcsaWorldKey[],
    key: TMcsaWorldKey,
): TMcsaWorldStatus => {
    if (completedKeys.includes(key)) return 'done';

    const current = worlds.find(world => !completedKeys.includes(world.key));
    if (current?.key === key) return 'current';

    return 'locked';
};

export const getCurrentWorld = (
    worlds: IMcsaWorld[],
    completedKeys: TMcsaWorldKey[],
): IMcsaWorld | null => {
    return worlds.find(world => !completedKeys.includes(world.key)) ?? null;
};

export const getExplorerPoint = (
    worlds: IMcsaWorld[],
    completedKeys: TMcsaWorldKey[],
): IMcsaMapPoint => {
    const lastCompleted = [...worlds].reverse().find(world => completedKeys.includes(world.key));

    if (!lastCompleted) return MCSA_MAP_START;

    const layout = MCSA_WORLD_LAYOUT[lastCompleted.key];
    return { x: layout.x, y: layout.y };
};

export const emptyAnswers = (): Record<TMcsaWorldKey, Record<number, TMcsaAnswer>> => ({
    cur: {},
    travel: {},
    shop: {},
    hobby: {},
    mingle: {},
    online: {},
});

export const getWorldGames = (world: IMcsaWorld, phase: TMcsaWorldGamePhase): IMcsaWorldGame[] => {
    return world.games.filter(game => game.phase === phase);
};

export const isDualAnswer = (answer: TMcsaAnswer | undefined): answer is IMcsaDualAnswer => {
    return typeof answer === 'object' && answer !== null;
};

export const isQuestionAnswered = (mech: TMcsaQuestionMech, answer: TMcsaAnswer | undefined) => {
    if (mech === 'dual') {
        return isDualAnswer(answer) && answer.like != null && answer.risk != null;
    }

    return typeof answer === 'number';
};

export const getAnswerLabel = (
    labels: readonly string[],
    question: IMcsaQuestion,
    answer: number,
) => {
    const pole = answer < 2 ? question.a : question.b;
    return `${labels[answer]} — ${pole}`;
};

export const getCatalogGame = (
    catalog: Record<string, IMcsaGameCatalogItem>,
    key: string,
): IMcsaGameCatalogItem | undefined => {
    return catalog[key];
};

const PROGRESS_KEY = 'ms_journey_progress';

export interface IMcsaJourneyProgress {
    completedKeys: TMcsaWorldKey[];
    answers: Record<TMcsaWorldKey, Record<number, TMcsaAnswer>>;
    shopQueue: number[];
    wildcardSel: number | null;
    wildcardRevealed: boolean;
}

export const emptyJourneyProgress = (): IMcsaJourneyProgress => ({
    completedKeys: [],
    answers: emptyAnswers(),
    shopQueue: [],
    wildcardSel: null,
    wildcardRevealed: false,
});

export const loadMcsaJourneyProgress = (): IMcsaJourneyProgress => {
    try {
        const raw = sessionStorage.getItem(PROGRESS_KEY);
        if (!raw) return emptyJourneyProgress();
        const parsed = JSON.parse(raw) as Partial<IMcsaJourneyProgress>;
        return {
            ...emptyJourneyProgress(),
            ...parsed,
            answers: { ...emptyAnswers(), ...(parsed.answers || {}) },
            completedKeys: Array.isArray(parsed.completedKeys) ? parsed.completedKeys : [],
            shopQueue: Array.isArray(parsed.shopQueue) ? parsed.shopQueue : [],
        };
    } catch {
        return emptyJourneyProgress();
    }
};

export const saveMcsaJourneyProgress = (progress: IMcsaJourneyProgress) => {
    try {
        sessionStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
    } catch {
        // ignore
    }
};
